import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isAdminAsMember: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, churchName?: string, address?: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminAsMember, setIsAdminAsMember] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    // 통합관리자 세션 확인
    const adminSession = sessionStorage.getItem('admin_session') === 'true';
    const adminUsername = sessionStorage.getItem('admin_username');
    
    if (adminSession && adminUsername === 'wonderm') {
      setIsSuperAdmin(true);
      
      // 통합관리자 가상 프로필 생성
      const mockProfile: Profile = {
        id: 'super-admin-wonderm',
        email: 'wonderm@admin.talent.com',
        full_name: '통합관리자',
        church_name: '통합관리자',
        address: '',
        role: 'super_admin',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      // 가상 User 객체 생성
      const mockUser = {
        id: 'super-admin-wonderm',
        email: 'wonderm@admin.talent.com',
        app_metadata: {},
        user_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString(),
      } as User;
      
      setUser(mockUser);
      setProfile(mockProfile);
      setLoading(false);
      return;
    }

    // 일반 사용자 세션 확인
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // 인증 상태 변경 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // 통합관리자 세션이 있으면 무시
      if (sessionStorage.getItem('admin_session') === 'true') {
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        // 프로필이 없으면 자동 생성 시도
        if (error.code === 'PGRST116') {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (!user) {
            setLoading(false);
            return;
          }

          // 통합관리자 프로필 생성
          if (user.email === 'wonderm@admin.talent.com') {
            const { data: newProfile, error: insertError } = await supabase
              .from('profiles')
              .insert({
                id: userId,
                email: user.email,
                full_name: '통합관리자',
                church_name: '통합관리자',
                address: '',
                role: 'super_admin',
              })
              .select()
              .single();

            if (insertError) {
              setLoading(false);
              return;
            }

            if (newProfile) {
              setProfile(newProfile);
              
              // 포인트도 생성
              await supabase
                .from('talent_points')
                .insert({
                  user_id: userId,
                  balance: 1000000,
                })
                .select()
                .single();
              
              setLoading(false);
              return;
            }
          }

          // 일반 사용자 프로필 자동 생성
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              email: user.email || '',
              full_name: user.user_metadata?.full_name || '사용자',
              church_name: user.user_metadata?.church_name || '',
              address: user.user_metadata?.address || '',
              role: user.user_metadata?.role || 'member',
            })
            .select()
            .single();

          if (insertError) {
            // 재시도 (Edge Function이 처리 중일 수 있음)
            setTimeout(() => loadProfile(userId), 2000);
            return;
          }

          if (newProfile) {
            setProfile(newProfile);
            
            // 달란트 포인트도 생성
            await supabase
              .from('talent_points')
              .insert({
                user_id: userId,
                balance: 0,
                total_earned: 0,
                total_spent: 0,
              })
              .select()
              .single();
            
            setLoading(false);
            return;
          }
        }
        
        setLoading(false);
        return;
      }
      
      setProfile(data);
    } catch (error) {
      // 오류를 조용히 처리
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // 통합관리자 계정 체크
      if ((email === 'wonderm' || email === 'wonderm@admin.talent.com') && password === '*2025wonderm') {
        // 통합관리자 세션 설정
        sessionStorage.setItem('admin_session', 'true');
        sessionStorage.setItem('admin_username', 'wonderm');
        
        setIsSuperAdmin(true);
        
        // 통합관리자 가상 프로필 생성
        const mockProfile: Profile = {
          id: 'super-admin-wonderm',
          email: 'wonderm@admin.talent.com',
          full_name: '통합관리자',
          church_name: '통합관리자',
          address: '',
          role: 'super_admin',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        // 가상 User 객체 생성
        const mockUser = {
          id: 'super-admin-wonderm',
          email: 'wonderm@admin.talent.com',
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated',
          created_at: new Date().toISOString(),
        } as User;
        
        setUser(mockUser);
        setProfile(mockProfile);
        setLoading(false);
        return;
      }

      // 일반 사용자 로그인
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('로그인 오류:', error);
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('이메일 또는 비밀번호가 일치하지 않습니다');
        }
        if (error.message.includes('Email not confirmed')) {
          throw new Error('이메일 인증이 필요합니다');
        }
        if (error.message.includes('network')) {
          throw new Error('인터넷 연결을 확인해주세요');
        }
        throw new Error('로그인할 수 없습니다. 다시 시도해주세요');
      }
    } catch (error: any) {
      console.error('로그인 실패:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, fullName: string, churchName?: string, address?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            church_name: churchName,
            address: address,
            role: 'member',
          },
          emailRedirectTo: undefined,
        },
      });

      if (error) {
        if (error.message.includes('already registered')) {
          throw new Error('이미 가입된 이메일입니다');
        }
        if (error.message.includes('password')) {
          throw new Error('비밀번호는 6자 이상이어야 합니다');
        }
        if (error.message.includes('email')) {
          throw new Error('올바른 이메일 주소를 입력해주세요');
        }
        throw new Error('회원가입할 수 없습니다. 다시 시도해주세요');
      }

      if (!data.user) {
        throw new Error('회원가입할 수 없습니다. 다시 시도해주세요');
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/handle-new-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: data.user.id,
          email: email,
          fullName: fullName,
          churchName: churchName,
          address: address,
          role: 'member',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('프로필 생성 오류:', errorData);
        
        if (errorData.error) {
          const errorMsg = errorData.error.toLowerCase();
          if (errorMsg.includes('foreign key constraint') || errorMsg.includes('violates foreign key')) {
            throw new Error('연결된 데이터가 없어 처리할 수 없습니다');
          }
          if (errorMsg.includes('duplicate key') || errorMsg.includes('unique constraint')) {
            throw new Error('이미 존재하는 데이터입니다');
          }
          if (errorMsg.includes('not-null constraint') || errorMsg.includes('null value')) {
            throw new Error('필수 정보가 입력되지 않았습니다');
          }
          if (errorMsg.includes('permission denied')) {
            throw new Error('접근 권한이 없습니다');
          }
        }
        throw new Error('프로필 생성 중 오류가 발생했습니다');
      }

      if (data.session) {
        await loadProfile(data.user.id);
      }
    } catch (error: any) {
      console.error('회원가입 실패:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // 통합관리자 세션 정리
      if (isSuperAdmin) {
        sessionStorage.removeItem('admin_session');
        sessionStorage.removeItem('admin_username');
        setIsSuperAdmin(false);
        setIsAdminAsMember(false);
        setUser(null);
        setProfile(null);
        window.location.href = '/';
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw new Error('로그아웃할 수 없습니다. 다시 시도해주세요');
    } catch (error: any) {
      console.error('로그아웃 실패:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('로그인이 필요합니다');

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) {
        console.error('프로필 업데이트 오류:', error);
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('foreign key constraint') || errorMsg.includes('violates foreign key')) {
          throw new Error('연결된 데이터가 없어 처리할 수 없습니다');
        }
        if (errorMsg.includes('duplicate key') || errorMsg.includes('unique constraint')) {
          throw new Error('이미 존재하는 데이터입니다');
        }
        if (errorMsg.includes('not-null constraint') || errorMsg.includes('null value')) {
          throw new Error('필수 정보가 입력되지 않았습니다');
        }
        if (errorMsg.includes('permission denied')) {
          throw new Error('접근 권한이 없습니다');
        }
        
        throw new Error('프로필을 수정할 수 없습니다. 다시 시도해주세요');
      }

      await loadProfile(user.id);
    } catch (error: any) {
      console.error('프로필 업데이트 실패:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        isAdminAsMember,
        isSuperAdmin,
        signIn,
        signUp,
        signOut,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth는 AuthProvider 내에서 사용해야 합니다');
  }
  return context;
}
